// System Design fundamentals, part 2: networking and APIs, estimation,
// object storage and CDNs, search, and IDs and time.

export const systemDesignCore2 = [
  {
    slug: 'networking-and-apis',
    title: 'Networking and API Design',
    summary: 'What happens on the wire, REST vs GraphQL vs gRPC, API gateways, and designing APIs that age well.',
    readTime: 11,
    content: [
      { type: 'h2', text: 'Why this matters' },
      { type: 'p', text: 'Every design starts with clients talking to servers and services talking to each other. Knowing what a request costs on the network, and which API style fits which job, lets you make sensible choices quickly and explain latency numbers with confidence.' },

      { type: 'h2', text: 'The mental model: a letter through a postal system' },
      { type: 'p', text: 'DNS is the address book that turns a name into an address. TCP is a reliable courier that confirms every parcel arrived in order. TLS seals the envelope so nobody can read or change it. HTTP is the format of the letter itself. Each layer adds a little time, which is why connection reuse and nearby servers matter so much.' },

      { type: 'h2', text: 'The cost of a request' },
      { type: 'table', headers: ['Step', 'Rough cost', 'How to reduce it'], rows: [
        ['DNS lookup', 'A few ms to 100+ ms on a cold lookup', 'DNS caching, fewer distinct domains'],
        ['TCP handshake', 'One round trip', 'Keep-alive and connection pooling'],
        ['TLS handshake', 'One more round trip (TLS 1.3)', 'Session resumption, HTTP/3'],
        ['Request + response', 'At least one round trip, plus server time', 'Servers and CDNs close to users, smaller payloads'],
        ['Round trip across continents', '~150–250 ms', 'Regional deployments, edge caching']
      ] },
      { type: 'tip', text: 'Round trips dominate latency far more than bandwidth for typical API calls. Ten sequential calls between services in different regions can easily cost two seconds; batching them or keeping chatty services in the same data centre matters more than faster code.' },

      { type: 'h2', text: 'Choosing an API style' },
      { type: 'table', headers: ['Style', 'Strengths', 'Weaknesses', 'Good fit'], rows: [
        ['REST over HTTP/JSON', 'Simple, universal, cacheable with HTTP', 'Over- or under-fetching; many round trips for complex screens', 'Public APIs, most web and mobile backends'],
        ['GraphQL', 'Clients ask for exactly the fields they need in one request', 'Caching, rate limiting and query cost control are harder', 'Many different screens or clients over the same data'],
        ['gRPC', 'Fast binary format, strict contracts, streaming', 'Not browser-native; harder to debug by hand', 'Internal service-to-service calls'],
        ['Events / messages', 'Decoupled, asynchronous', 'Eventual consistency, harder to trace', 'Notifying other services that something happened']
      ] },

      { type: 'h2', text: 'REST design that ages well' },
      { type: 'code', language: 'http', code: `GET    /courses?level=beginner&cursor=abc&limit=20   list with filters and pagination
GET    /courses/42                                  read one
POST   /courses                                     create → 201 + Location header
PATCH  /courses/42                                  partial update
DELETE /courses/42                                  delete → 204
POST   /courses/42/publish                          an action that isn't plain CRUD` },
      { type: 'ul', items: [
        'Plural nouns for resources; HTTP methods for actions.',
        'Correct status codes: 400 invalid input, 401 not signed in, 403 not allowed, 404 not found, 409 conflict, 429 rate limited.',
        'Cursor pagination for anything that can grow.',
        'Additive changes only within a version: add fields, never rename or remove them.',
        'Idempotency keys for POSTs that must not run twice, such as payments.'
      ] },

      { type: 'h2', text: 'API gateways' },
      { type: 'p', text: 'An API gateway is a single entry point in front of many services. It routes requests, terminates TLS, checks authentication, applies rate limits, and collects logs and metrics. That keeps cross-cutting concerns in one place, so individual services can focus on business logic.' },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Chatty designs that need many sequential calls to render one screen.',
        'Returning 200 with an error message in the body.',
        'Offset pagination on large or fast-changing lists.',
        'Breaking existing clients by renaming fields.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'What happens when you type a URL into a browser?', answer: ['The browser resolves the domain with DNS, opens a TCP connection (or QUIC for HTTP/3), performs a TLS handshake, sends the HTTP request, and receives the response. It then parses HTML, loads CSS and JavaScript, builds the page and renders it, fetching further resources along the way, often from a CDN.'] },
      { type: 'qa', question: 'When would you use gRPC instead of REST?', answer: ['For internal service-to-service calls where performance, strict typed contracts and streaming matter, and where both sides are services you control. Public and browser-facing APIs are usually simpler with REST or GraphQL.'] },
      { type: 'qa', question: 'How do you version an API?', answer: ['Avoid breaking changes as long as possible by only adding fields. When a breaking change is unavoidable, introduce a new version (for example `/v2`), run both in parallel, and give clients a migration period with clear deprecation notices.'] },

      { type: 'exercise', title: 'Design the API for a course platform', task: 'Learners browse courses, enrol (paid), watch lessons and track progress. Write the main endpoints with methods, paths and key status codes, and mark which ones need idempotency.', solution: `GET   /courses?topic=&level=&cursor=          200
GET   /courses/{id}                           200 | 404
POST  /courses/{id}/enrolments                201 | 409 already enrolled
      (Idempotency-Key header: payment involved)
GET   /me/enrolments                          200
GET   /courses/{id}/lessons                   200 | 403 not enrolled
PUT   /me/progress/{lessonId}  { completed }  204  (PUT is naturally idempotent)`, language: 'text' },

      { type: 'summary', items: [
        'Round trips, not bandwidth, usually dominate request latency.',
        'REST for most APIs, GraphQL for varied client needs, gRPC internally, events for decoupling.',
        'Design REST around resources, correct status codes and cursor pagination.',
        'Gateways centralise routing, auth, limits and observability.'
      ] }
    ]
  },
  {
    slug: 'back-of-envelope-estimation',
    title: 'Back-of-the-Envelope Estimation',
    summary: 'Numbers worth remembering, fast estimation techniques, and turning estimates into design decisions.',
    readTime: 10,
    content: [
      { type: 'h2', text: 'Why this matters' },
      { type: 'p', text: 'Whether a design needs one database or fifty depends on numbers. Quick, rough estimates tell you whether you need caching, sharding, a CDN or a queue, and they show the interviewer that your decisions are grounded rather than guessed.' },

      { type: 'h2', text: 'The mental model: an order of magnitude is enough' },
      { type: 'p', text: 'You are not filling in a spreadsheet; you are deciding which category a system falls into. Being off by a factor of two rarely changes a design. Being off by a factor of a hundred always does. Round boldly and keep moving.' },

      { type: 'h2', text: 'Numbers worth remembering' },
      { type: 'table', headers: ['Quantity', 'Approximate value'], rows: [
        ['Seconds in a day', '~86,400 ≈ 10⁵'],
        ['Requests per day → per second', 'divide by ~10⁵ (1M/day ≈ 12/s)'],
        ['Peak traffic', '2–3× the average, more for spiky products'],
        ['Memory access', '~100 ns'],
        ['SSD random read', '~100 µs'],
        ['Round trip in the same data centre', '~0.5 ms'],
        ['Round trip across continents', '~150 ms'],
        ['Simple key lookups on one Redis node', '~100,000 per second'],
        ['Writes on one well-tuned relational primary', 'thousands per second'],
        ['A typical stateless API server', 'hundreds to a few thousand simple requests per second']
      ] },
      { type: 'callout', variant: 'note', title: 'Treat these as rough guides', text: 'Real capacity depends heavily on hardware, data size and query complexity. In an interview, state your assumption (“assume a server handles about 1,000 requests a second”) and move on.' },

      { type: 'h2', text: 'A worked example: a photo-sharing app' },
      { type: 'code', language: 'text', code: `Users          50M daily active users
Uploads        each uploads 0.2 photos/day    → 10M photos/day   ≈ 115 writes/s (peak ~350)
Views          each views 60 photos/day       → 3B views/day     ≈ 35,000 reads/s (peak ~100k)
Ratio          ~300 reads per write           → caching and a CDN are essential

Photo size     ~1 MB original + thumbnails ≈ 1.3 MB
Storage/day    10M × 1.3 MB ≈ 13 TB/day       → object storage, not a database
Per year       ~13 TB × 365 ≈ 4.7 PB

Bandwidth      35,000 views/s × ~200 KB per thumbnail ≈ 7 GB/s
                                              → serve from a CDN` },

      { type: 'h2', text: 'Turning numbers into decisions' },
      { type: 'table', headers: ['If your estimate shows…', 'Then consider…'], rows: [
        ['Reads ≫ writes', 'Caching, read replicas, CDN'],
        ['Writes beyond one primary’s capacity', 'Sharding, write-optimised stores, batching'],
        ['Large binary files', 'Object storage plus a CDN, never the database'],
        ['Data that fits in memory', 'An in-memory store or cache for the hot set'],
        ['Slow work per request', 'Queues and background workers'],
        ['Global users with tight latency targets', 'Regional deployments and edge caching']
      ] },

      { type: 'h2', text: 'Techniques that keep you fast' },
      { type: 'ul', items: [
        'Work in powers of ten and round at every step.',
        'Write units next to every number (requests/s, GB/day).',
        'Estimate the average, then multiply for peak.',
        'Sanity-check: does 7 GB/s of egress sound like a lot? Yes, so a CDN is needed.'
      ] },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Precise arithmetic that wastes five minutes.',
        'Estimates that never change the design.',
        'Forgetting peaks, replication and growth over time.',
        'Mixing up bits and bytes, or per day and per second.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'How many servers do you need for 50,000 requests per second?', answer: ['State an assumption per server, for example 1,000 requests per second for a typical API workload. That gives 50 servers at average load; add headroom for peaks and failures (say 2×), so roughly 100, then note that the database and cache need separate sizing.'] },
      { type: 'qa', question: 'Why estimate storage at all?', answer: ['It decides the storage technology and whether data must be sharded or tiered. A few hundred GB fits one database; petabytes of media need object storage and lifecycle rules to move old data to cheaper tiers.'] },

      { type: 'exercise', title: 'Estimate a messaging app', task: '20M daily users, each sends 30 messages a day of about 100 bytes, and each message is read by 2 people on average. Estimate writes per second, reads per second, daily storage and five-year storage, then name two design decisions these numbers drive.', solution: `Messages/day   20M × 30 = 600M           ≈ 7,000 writes/s (peak ~20k)
Reads          600M × 2 = 1.2B/day        ≈ 14,000 reads/s (peak ~40k)
Storage/day    600M × ~200 B with metadata ≈ 120 GB/day
Five years     120 GB × 365 × 5            ≈ 220 TB

Decisions
- 220 TB and ~20k writes/s at peak → a horizontally scalable store,
  partitioned by conversation
- Reads mostly hit recent messages → cache recent history per conversation`, language: 'text' },

      { type: 'summary', items: [
        'One day ≈ 10⁵ seconds; 1M per day ≈ 12 per second.',
        'Estimate average, then peak, then storage and bandwidth.',
        'Round boldly; orders of magnitude are what matter.',
        'Every estimate should lead to a design decision.'
      ] }
    ]
  },
  {
    slug: 'object-storage-and-cdns',
    title: 'Object Storage, CDNs and File Uploads',
    summary: 'Storing large files cheaply, serving them fast worldwide, and uploading them without overloading your servers.',
    readTime: 10,
    content: [
      { type: 'h2', text: 'Why this matters' },
      { type: 'p', text: 'Images, videos, documents and backups are huge compared with ordinary records. Putting them in a database or pushing them through your API servers is slow and expensive. Object storage and CDNs are the standard answer, and the upload flow is a frequent deep dive.' },

      { type: 'h2', text: 'The mental model: a warehouse and local shops' },
      { type: 'p', text: 'Object storage is a vast, cheap warehouse that never loses anything. A CDN is a chain of local shops that keep copies of popular items close to customers, so most people never travel to the warehouse.' },

      { type: 'h2', text: 'Object storage' },
      { type: 'ul', items: [
        'Stores files (“objects”) by key, for example `users/42/avatar.jpg`, in buckets.',
        'Practically unlimited capacity, very high durability, low cost per GB.',
        'Accessed over HTTP; not a file system and not for frequently updated small records.',
        'Storage classes and lifecycle rules move old data to cheaper tiers automatically.'
      ] },
      { type: 'p', text: 'The database stores metadata (owner, file key, size, status, permissions); the bytes live in object storage.' },

      { type: 'h2', text: 'Uploading with pre-signed URLs' },
      { type: 'p', text: 'Routing uploads through your API servers wastes their bandwidth and memory. Instead, the server authorises the upload and returns a short-lived signed URL; the client uploads directly to storage.' },
      {"type": "diagram", "title": "Direct uploads with pre-signed URLs", "caption": "The API authorises the upload and returns a signed URL; the file goes straight to object storage. Processing runs asynchronously, and files are served to users through the CDN.", "cols": 4, "rows": 3, "nodes": [{"id": "client", "label": "Client", "kind": "client", "col": 0, "row": 1}, {"id": "api", "label": "API", "kind": "service", "col": 1, "row": 0, "sub": "checks size & quota"}, {"id": "db", "label": "File metadata", "kind": "db", "col": 2, "row": 0}, {"id": "store", "label": "Object storage", "kind": "storage", "col": 2, "row": 1}, {"id": "w", "label": "Media workers", "kind": "worker", "col": 3, "row": 1, "sub": "thumbnails, scans"}, {"id": "cdn", "label": "CDN", "kind": "edge", "col": 1, "row": 2}], "edges": [{"from": "client", "to": "api", "label": "1. request URL"}, {"from": "api", "to": "db", "label": "create row"}, {"from": "client", "to": "store", "label": "2. PUT file"}, {"from": "store", "to": "w", "label": "3. uploaded event", "dashed": true}, {"from": "cdn", "to": "store", "label": "origin fetch"}, {"from": "client", "to": "cdn", "label": "download"}]},
      { type: 'tip', text: 'For very large files use multipart upload: split the file into parts, upload them in parallel, retry only failed parts, then complete the upload. It also lets uploads resume after a network drop.' },

      { type: 'h2', text: 'CDNs' },
      { type: 'p', text: 'A content delivery network caches responses at edge locations around the world. Users download from a nearby edge instead of your origin, which cuts latency and dramatically reduces load and bandwidth costs at the origin.' },
      { type: 'table', headers: ['Content', 'CDN strategy'], rows: [
        ['Versioned static assets (app.3f9a.js)', 'Cache for a year; the file name changes on every release'],
        ['Images and video', 'Cache long; use different keys for different sizes or formats'],
        ['Public API responses', 'Short TTLs, stale-while-revalidate'],
        ['Private files', 'Signed URLs or signed cookies that expire']
      ] },

      { type: 'h2', text: 'Processing media' },
      { type: 'p', text: 'After upload, a queue triggers workers to create thumbnails, transcode video, extract metadata and scan for malware. The file is shown as ready only when processing finishes, and each step should be idempotent so retries are safe.' },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Storing large files as database blobs.',
        'Proxying uploads and downloads through API servers.',
        'Trusting the client’s file name and content type.',
        'Long-lived or public URLs for private content.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'How do you serve private files through a CDN?', answer: ['Use signed URLs (or signed cookies) generated by your API after checking permissions. They include an expiry and a signature the CDN verifies, so a leaked link stops working after a short time.'] },
      { type: 'qa', question: 'How do you invalidate a cached image that changed?', answer: ['Prefer versioned keys: upload the new image under a new key and update the reference, so caches never serve the old one. CDN purge requests also work but are slower and harder to manage at scale.'] },

      { type: 'exercise', title: 'Design profile picture uploads', task: 'Users upload a profile picture (up to 10 MB). It must be resized to three sizes, served fast worldwide, and old pictures must not be shown after a change. Describe the flow.', solution: `1. API validates size/type, returns a pre-signed URL for
   uploads/{userId}/{uuid}.jpg
2. Client uploads directly to object storage
3. Upload event → queue → worker resizes to 64/256/1024 px,
   stores avatars/{userId}/{uuid}_{size}.webp
4. Worker updates users.avatar_key = {uuid} only when all sizes exist
5. Pages reference CDN URLs containing the uuid, so a new picture
   means new URLs and no stale cache
6. Old files are deleted by a cleanup job after a delay`, language: 'text' },

      { type: 'summary', items: [
        'Bytes in object storage, metadata in the database.',
        'Pre-signed URLs let clients upload directly; multipart for large files.',
        'CDNs serve content near users; version keys instead of purging.',
        'Process media asynchronously and mark files ready when done.'
      ] }
    ]
  },
  {
    slug: 'search-systems',
    title: 'Search Systems and Inverted Indexes',
    summary: 'How full-text search works, keeping a search index in sync, relevance, and autocomplete basics.',
    readTime: 10,
    content: [
      { type: 'h2', text: 'Why this matters' },
      { type: 'p', text: 'Almost every product has a search box, and `LIKE \'%word%\'` stops working the moment data grows. Understanding inverted indexes and how to keep a search engine in sync with your database lets you add real search to any design.' },

      { type: 'h2', text: 'The mental model: the index at the back of a book' },
      { type: 'p', text: 'Instead of reading every page to find “caching”, you look up the word in the index, which lists the pages it appears on. An inverted index does the same: for every word, it stores the list of documents containing it.' },

      { type: 'h2', text: 'How an inverted index works' },
      { type: 'code', variants: [
        { language: 'java', code: `import java.util.*;

public class InvertedIndex {
    static final Map<String, Set<Integer>> INDEX = new HashMap<>();

    static List<String> tokenize(String text) {
        return Arrays.stream(text.toLowerCase().split("\\\\W+")).filter(w -> !w.isEmpty()).toList();
    }

    static List<Integer> search(String query) {
        Set<Integer> result = null;
        for (String word : tokenize(query)) {
            Set<Integer> ids = INDEX.getOrDefault(word, Set.of());
            if (result == null) result = new TreeSet<>(ids);
            else result.retainAll(ids);                       // AND query
        }
        return new ArrayList<>(result);
    }

    public static void main(String[] args) {
        Map<Integer, String> docs = Map.of(1, "Learn React hooks", 2, "React performance tips", 3, "Learn Node streams");
        docs.forEach((id, text) -> tokenize(text).forEach(w -> INDEX.computeIfAbsent(w, k -> new HashSet<>()).add(id)));
        System.out.println(search("react") + " " + search("learn react") + " " + search("streams"));
    }
}`, output: `[1, 2] [1] [3]` },
        { language: 'python', code: `import re

docs = {1: "Learn React hooks", 2: "React performance tips", 3: "Learn Node streams"}

def tokenize(text):
    return [w for w in re.split(r"\\W+", text.lower()) if w]

index = {}
for doc_id, text in docs.items():
    for word in tokenize(text):
        index.setdefault(word, set()).add(doc_id)

def search(query):
    sets = [index.get(w, set()) for w in tokenize(query)]
    return sorted(set.intersection(*sets))          # AND query

print(search("react"), search("learn react"), search("streams"))`, output: `[1, 2] [1] [3]` },
        { language: 'javascript', code: `const docs = {
  1: "Learn React hooks",
  2: "React performance tips",
  3: "Learn Node streams"
};

const tokenize = (text) => text.toLowerCase().split(/\\W+/).filter(Boolean);

const index = {};
for (const [id, text] of Object.entries(docs)) {
  for (const word of tokenize(text)) (index[word] ??= new Set()).add(Number(id));
}

const search = (query) => {
  const sets = tokenize(query).map((w) => index[w] ?? new Set());
  return [...sets[0]].filter((id) => sets.every((s) => s.has(id)));   // AND query
};

console.log(search("react"), search("learn react"), search("streams"));`, output: `[ 1, 2 ] [ 1 ] [ 3 ]` }
      ] },
      { type: 'p', text: 'Real search engines add much more: stemming (“running” matches “run”), synonyms, typo tolerance, phrase queries, and relevance scoring such as BM25, which rewards rare words and matches in short fields like titles.' },

      { type: 'h2', text: 'Keeping the search index in sync' },
      { type: 'p', text: 'The database stays the source of truth; the search engine is a copy optimised for searching.' },
      { type: 'table', headers: ['Approach', 'How', 'Trade-off'], rows: [
        ['Dual write', 'Write the database, then the search engine, in the request', 'Simple, but a failure between the two leaves them inconsistent'],
        ['Events / outbox', 'Publish a change event; an indexer updates search', 'Reliable, slightly delayed'],
        ['Change data capture', 'Stream the database’s change log into the indexer', 'No application changes; more infrastructure'],
        ['Periodic rebuild', 'Reindex everything on a schedule', 'Good as a safety net, too slow alone']
      ] },
      { type: 'tip', text: 'Plan for full reindexing from day one: mapping changes and bugs eventually require it. Build a new index in the background, then switch an alias to it with no downtime.' },

      { type: 'h2', text: 'Relevance and filtering' },
      { type: 'ul', items: [
        'Boost important fields: a match in the title outranks one in the body.',
        'Combine text relevance with business signals: popularity, recency, rating.',
        'Apply filters (category, price range, permissions) inside the search query.',
        'Measure: track clicks on results and searches with no results.'
      ] },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Using the search engine as the primary database.',
        'Synchronous dual writes with no recovery path.',
        'Ignoring permissions, so users find documents they should not see.',
        'No plan for reindexing.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'Why is a database LIKE query not enough for search?', answer: ['A leading wildcard cannot use a normal index, so every row is scanned; and it has no relevance ranking, stemming, typo tolerance or multi-word matching. Inverted indexes are built exactly for these needs.'] },
      { type: 'qa', question: 'How would you search across 1 billion documents?', answer: ['Shard the index across many nodes (each holds part of the documents), query all shards in parallel, and merge the top results. Add replicas for read throughput and availability.'] },

      { type: 'exercise', title: 'Add search to a course catalogue', task: 'Users search 50,000 courses by title, description and instructor, filter by level and price, and sort by relevance or rating. Describe the data flow and index design.', solution: `Index fields: title (boosted), description, instructorName,
              level (keyword), price (number), rating (number), published
Sync: course changes → outbox event → indexer updates the document
Query: text match on title/description/instructor
       + filters level/price/published inside the query
       + sort by score (default) or rating
Safety: nightly job compares counts; full reindex via alias swap when needed`, language: 'text' },

      { type: 'summary', items: [
        'An inverted index maps words to the documents containing them.',
        'Keep the database as source of truth; sync search through events or CDC.',
        'Relevance combines text scoring with business signals.',
        'Shard and replicate search indexes for scale; plan for reindexing.'
      ] }
    ]
  },
  {
    slug: 'unique-ids-and-time',
    title: 'Unique IDs and Time in Distributed Systems',
    summary: 'Generating IDs without a single counter, why clocks lie, and ordering events across machines.',
    readTime: 9,
    content: [
      { type: 'h2', text: 'Why this matters' },
      { type: 'p', text: 'One database can hand out auto-increment IDs. Once you have many shards and services, you need IDs that are unique without asking a central counter every time, and ideally sortable by time. You also need to be careful with timestamps, because different machines disagree about what time it is.' },

      { type: 'h2', text: 'The mental model: ticket rolls' },
      { type: 'p', text: 'Instead of one ticket machine everyone queues at, give each counter its own roll of tickets with a unique prefix. Every ticket is still unique, and nobody waits for anybody else.' },

      { type: 'h2', text: 'ID strategies' },
      { type: 'table', headers: ['Strategy', 'Pros', 'Cons'], rows: [
        ['Database auto-increment', 'Simple, compact, ordered', 'Single point of coordination; leaks volume'],
        ['UUID v4 (random)', 'Generated anywhere, no coordination', '128 bits; random order hurts index locality'],
        ['UUID v7 / ULID', 'Generated anywhere, time-ordered', '128 bits'],
        ['Snowflake-style 64-bit', 'Compact, time-ordered, no coordination per ID', 'Needs unique worker IDs and reasonable clocks']
      ] },

      { type: 'h2', text: 'Snowflake-style IDs' },
      { type: 'p', text: 'A 64-bit number made of a timestamp, a worker ID and a per-millisecond sequence. IDs from the same worker always increase, and IDs from different workers never collide.' },
      { type: 'code', variants: [
        { language: 'java', code: `public class Snowflake {
    static final long EPOCH = 1_700_000_000_000L;      // a custom start time keeps the numbers smaller

    static long makeId(long timestampMs, long workerId, long sequence) {
        return ((timestampMs - EPOCH) << 22)   // 41 bits of time
             | (workerId << 12)                // 10 bits: up to 1,024 workers
             | sequence;                       // 12 bits: 4,096 IDs per ms per worker
    }

    public static void main(String[] args) {
        long id = makeId(1_700_000_123_456L, 7, 42);
        System.out.println(id + " -> time=" + ((id >> 22) + EPOCH)
            + ", worker=" + ((id >> 12) & 1023) + ", seq=" + (id & 4095));
    }
}`, output: `517812023338 -> time=1700000123456, worker=7, seq=42` },
        { language: 'python', code: `EPOCH = 1_700_000_000_000      # a custom start time keeps the numbers smaller

def make_id(timestamp_ms, worker_id, sequence):
    return ((timestamp_ms - EPOCH) << 22) | (worker_id << 12) | sequence

def parse_id(id_):
    return {"timestamp_ms": (id_ >> 22) + EPOCH, "worker_id": (id_ >> 12) & 1023, "sequence": id_ & 4095}

id_ = make_id(1_700_000_123_456, 7, 42)
print(id_, parse_id(id_))`, output: `517812023338 {'timestamp_ms': 1700000123456, 'worker_id': 7, 'sequence': 42}` },
        { language: 'javascript', code: `const EPOCH = 1700000000000n;        // a custom start time keeps the numbers smaller

function makeId(timestampMs, workerId, sequence) {
  return ((BigInt(timestampMs) - EPOCH) << 22n)   // 41 bits of time
       | (BigInt(workerId) << 12n)                // 10 bits: up to 1,024 workers
       | BigInt(sequence);                        // 12 bits: 4,096 IDs per ms per worker
}

function parseId(id) {
  return {
    timestampMs: Number((id >> 22n) + EPOCH),
    workerId: Number((id >> 12n) & 1023n),
    sequence: Number(id & 4095n)
  };
}

const id = makeId(1700000123456, 7, 42);
console.log(id.toString(), parseId(id));`, output: `517812023338 { timestampMs: 1700000123456, workerId: 7, sequence: 42 }` }
      ] },
      { type: 'tip', text: 'IDs bigger than 2⁵³ lose precision as JavaScript numbers. Send 64-bit IDs to browsers as strings in JSON.' },

      { type: 'h2', text: 'Why clocks cannot be trusted completely' },
      { type: 'ul', items: [
        'Machine clocks drift and are corrected by time synchronisation, sometimes jumping backwards.',
        'Two events on different machines can get timestamps in the wrong order.',
        'Phones and browsers can have wildly wrong clocks; never order user data by client time.'
      ] },
      { type: 'p', text: 'When order matters, assign a sequence in one place (per conversation, per account), use database-generated ordering, or use logical clocks that count events rather than read the wall clock.' },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Ordering chat messages by the sender’s device time.',
        'Sequential public IDs that let anyone count your orders or scrape records.',
        'Assuming timestamps from different servers are comparable to the millisecond.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'How do you generate unique IDs across 100 servers?', answer: ['Use a scheme that needs no per-ID coordination: random UUIDs, time-ordered UUID v7, or Snowflake-style IDs with a unique worker ID per server (assigned at startup from a coordination service or configuration).'] },
      { type: 'qa', question: 'What happens to a Snowflake generator if the clock moves backwards?', answer: ['It could produce duplicate or out-of-order IDs. Generators detect this and either wait until the clock catches up or refuse to generate IDs, and alert, rather than risk duplicates.'] },

      { type: 'exercise', title: 'Choose IDs', task: 'Pick an ID strategy for: (a) orders shown to customers on receipts, (b) internal log events at 2 million per second, (c) chat messages that must sort by time within a conversation.', solution: `(a) Random or scrambled IDs shown to users (no guessable sequence);
    internally any ID works
(b) Snowflake-style 64-bit: compact, no coordination, time-ordered
(c) A per-conversation sequence number for display order,
    plus any unique ID (e.g. UUID v7) as the primary key`, language: 'text' },

      { type: 'summary', items: [
        'Distributed IDs avoid a central counter: UUIDs, UUID v7, Snowflake.',
        'Time-ordered IDs keep database indexes efficient.',
        'Wall clocks drift; order critical events with sequences, not timestamps.',
        'Send 64-bit IDs to browsers as strings.'
      ] }
    ]
  }
];
